import React, { useState, useContext, useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import { UtensilsCrossed } from "lucide-react";
import { AuthContext } from "../../context/Auth/AuthContext";
import PauseModal from "../PauseModal";
import { useToast } from "../../hooks/useToast";
import api from "../../services/api";
import IconButtonWithTooltip from "../ui/IconButtonWithTooltip";

const useStyles = makeStyles((theme) => ({
  badge: {
    backgroundColor: "#FF9800",
    color: "#fff",
    height: 20,
    minWidth: 20,
    padding: "0 0px",
    fontSize: "0.75rem",
    fontWeight: 500,
    fontFamily: '"Segoe UI", "Helvetica Neue", Helvetica, "Lucida Grande", Arial, Ubuntu, Cantarell, "Fira Sans", sans-serif',
    lineHeight: 1,
    borderRadius: 10,
    animation: "$badgePulse 2s ease-in-out infinite",
  },
  "@keyframes badgePulse": {
    "0%, 100%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.55,
    },
  },
}));

const PauseButton = () => {
  const classes = useStyles();
  const toast = useToast();
  const { user, socket } = useContext(AuthContext);
  const [pauseMode, setPauseMode] = useState(false);
  const [pauseEndsAt, setPauseEndsAt] = useState(null);
  const [, setPauseDuration] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [remainingMinutes, setRemainingMinutes] = useState(null);

  // Calcular minutos restantes
  useEffect(() => {
    if (!pauseMode || !pauseEndsAt) {
      setRemainingMinutes(null);
      return;
    }

    const calculateRemaining = () => {
      const now = new Date();
      const end = new Date(pauseEndsAt);
      const diffMs = end.getTime() - now.getTime();
      const diff = Math.floor(diffMs / 1000 / 60);
      
      // Se o tempo expirou (0 ou negativo)
      if (diff <= 0) {
        setPauseMode(false);
        setPauseEndsAt(null);
        setPauseDuration(0);
        setRemainingMinutes(null);
        toast.info("Sua pausa terminou automaticamente.");
        return; // Parar de calcular
      }
      
      setRemainingMinutes(diff);
    };

    calculateRemaining();
    const interval = setInterval(calculateRemaining, 10000); // Verificar a cada 10 segundos

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pauseMode, pauseEndsAt]);

  // Sincronizar com dados do usuário - APENAS se os campos de pausa existirem
  useEffect(() => {
    if (user && typeof user.pauseMode !== 'undefined') {
      setPauseMode(user.pauseMode || false);
      setPauseEndsAt(user.pauseEndsAt || null);
      setPauseDuration(user.pauseDuration || 0);
    }
  }, [user]);

  // Listeners Socket.IO
  useEffect(() => {
    if (!socket) return;

    const companyId = user?.companyId;
    if (!companyId) return;

    const handlePauseUpdate = (data) => {
      if (data.user?.id === user?.id) {
        if (data.action === "pause:on") {
          setPauseMode(true);
          setPauseEndsAt(data.user.pauseEndsAt);
          setPauseDuration(data.user.pauseDuration);
        } else if (data.action === "pause:off") {
          setPauseMode(false);
          setPauseEndsAt(null);
          setPauseDuration(0);
          setRemainingMinutes(null);
        } else if (data.action === "pause:extended") {
          setPauseEndsAt(data.user.pauseEndsAt);
          setPauseDuration(data.user.pauseDuration);
        }
      }
    };

    const handlePauseWarning = (data) => {
      toast.info(data.message || "Sua pausa termina em 5 minutos", {
        autoClose: 10000,
      });
    };

    socket.on(`company-${companyId}-user-pause`, handlePauseUpdate);
    socket.on(`user-${user.id}-pause-warning`, handlePauseWarning);

    return () => {
      socket.off(`company-${companyId}-user-pause`, handlePauseUpdate);
      socket.off(`user-${user.id}-pause-warning`, handlePauseWarning);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user]);

  const handleTogglePause = async () => {
    // Evitar warning de acessibilidade quando o modal abre (aria-hidden)
    if (document?.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (pauseMode) {
      // Desativar pausa
      try {
        await api.post("/users/unpause");
        setPauseMode(false);
        setPauseEndsAt(null);
        setPauseDuration(0);
        setRemainingMinutes(null);
        toast.success("Modo pausa desativado");
      } catch (error) {
        console.error("Erro ao desativar pausa:", error);
        toast.error("Erro ao desativar modo pausa");
      }
    } else {
      // Abrir modal para ativar pausa
      setModalOpen(true);
    }
  };

  const handleActivatePause = async (duration) => {
    try {
      const { data } = await api.post("/users/pause", { duration });

      setPauseMode(true);
      setPauseEndsAt(data.user.pauseEndsAt);
      setPauseDuration(duration);

      // Formatar duração para exibição
      let durationText;
      if (duration >= 60) {
        const h = Math.floor(duration / 60);
        const m = duration % 60;
        durationText = m > 0 ? `${h}h${m}min` : `${h}h`;
      } else {
        durationText = `${duration} minutos`;
      }
      const message = `Modo pausa ativado por ${durationText}`;

      toast.success(message, {
        autoClose: 5000,
      });

      setModalOpen(false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error ||
                          error?.response?.data?.message ||
                          error?.message ||
                          "Erro ao ativar modo pausa";

      toast.error(errorMessage);
    }
  };

  const getTooltipText = () => {
    if (!pauseMode) {
      return "Ativar modo pausa";
    }

    if (remainingMinutes !== null) {
      return `Pausa ativa (${remainingMinutes} min restantes)`;
    }

    return "Pausa ativa";
  };

  // Badge props quando em pausa com tempo definido
  const badgeProps = pauseMode && remainingMinutes !== null ? {
    badgeContent: remainingMinutes,
    color: "error",
    classes: { badge: classes.badge }
  } : undefined;

  return (
    <>
      <IconButtonWithTooltip
        icon={UtensilsCrossed}
        tooltip={getTooltipText()}
        onClick={handleTogglePause}
        iconColor={pauseMode ? "#FF9800" : undefined}
        badgeProps={badgeProps}
        aria-label="Modo Pausa"
      />

      <PauseModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleActivatePause}
      />
    </>
  );
};

export default PauseButton;

